import {
  IntegrationDetail,
  useGetStepCatalogQuery,
} from "features/integrationsBeta/integrationsBeta";
import { useCreateStepMutation } from "features/integrationsBeta/registration";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useMemo, useState } from "react";
import { formatApiError } from "utils/formatApiError";
import StepFormDialog, { StepFormValues } from "./StepFormDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  integration: IntegrationDetail;
}

/**
 * Settings-tab wrapper around the shared step form: picks a step from the
 * catalog and POSTs it to the existing integration immediately.
 */
export default function AddStepDialog({ open, onClose, integration }: Props) {
  const dispatch = useAppDispatch();
  // Warm the catalog cache before the form opens (shared query).
  useGetStepCatalogQuery();
  const [createStep, { isLoading: saving }] = useCreateStepMutation();
  const [apiError, setApiError] = useState<string | null>(null);

  const nextStageOrder = useMemo(
    () =>
      integration.steps.reduce((max, s) => Math.max(max, s.stage_order), 0) + 1,
    [integration.steps],
  );

  const handleSubmit = async (values: StepFormValues) => {
    try {
      await createStep({
        integrationId: integration.id,
        stage: values.stage,
        stage_order: values.stage_order,
        step_key: values.entry.step_key,
        name: values.name,
        is_enabled: true,
        connection: values.connection,
        config: values.config,
      }).unwrap();
      dispatch(
        pushNotification({ severity: "success", message: "Step added." }),
      );
      setApiError(null);
      onClose();
    } catch (err) {
      setApiError(
        formatApiError(
          (err as { data?: unknown })?.data,
          "Could not add the step.",
        ),
      );
    }
  };

  return (
    <StepFormDialog
      open={open}
      onClose={onClose}
      clientId={integration.client}
      nextStageOrder={nextStageOrder}
      submitting={saving}
      submitError={apiError}
      onSubmit={handleSubmit}
    />
  );
}
